import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Wat is een freelance contract?',
    answer: 'Een freelance contract is een juridisch bindende overeenkomst tussen een freelancer (zelfstandige) en een opdrachtgever. Het legt alle afspraken vast over de opdracht, vergoeding, deadlines, intellectueel eigendom en aansprakelijkheid. Zonder contract loop je als freelancer grote risico\'s op onbetaalde facturen en juridische conflicten.',
  },
  {
    question: 'Wat moet er in een freelance contract staan?',
    answer: 'Een goed freelance contract bevat minimaal: een duidelijke omschrijving van de opdracht en deliverables, het afgesproken tarief en betalingsvoorwaarden, de looptijd en deadlines, afspraken over intellectueel eigendom, een geheimhoudingsclausule (NDA), aansprakelijkheidsbeperking en opzeggingsvoorwaarden.',
  },
  {
    question: 'Is een freelance contract juridisch bindend?',
    answer: 'Ja, een freelance contract is juridisch bindend zodra beide partijen het hebben ondertekend. Het contract is afdwingbaar voor de rechtbank en geeft beide partijen wettelijke bescherming. Zorg er altijd voor dat het contract duidelijk is opgesteld en door beide partijen is ondertekend.',
  },
  {
    question: 'Kunnen freelancers een gratis template gebruiken?',
    answer: 'Ja, met Pactorae kun je gratis een professioneel freelance contract genereren dat is afgestemd op jouw specifieke situatie. Onze generator stelt de juiste vragen en maakt in 30 seconden een juridisch correct contract op maat voor België of Nederland.',
  },
  {
    question: 'Is een freelance contract verplicht in België?',
    answer: 'Een schriftelijk freelance contract is niet wettelijk verplicht in België, maar het is sterk aanbevolen. Zonder contract is het moeilijk om afspraken te bewijzen bij een geschil. Bovendien vereisen veel opdrachtgevers en bedrijven een ondertekend contract voordat ze met een freelancer samenwerken.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/freelance-contract-template',
      url: 'https://pactorae.com/freelance-contract-template',
      name: 'Freelance Contract Template (België & Nederland) | Pactorae',
      description:
        'Gratis freelance contract template voor België en Nederland. Professionele freelance overeenkomst met betalingsvoorwaarden, intellectueel eigendom en alle standaard clausules.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-[#2F4E6F] pr-4">{faq?.question}</span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
          style={{ backgroundColor: '#FF8C00', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const ContractPreview = () => (
  <div className="relative">
    {/* Paper document */}
    <div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      style={{ maxWidth: '720px', margin: '0 auto' }}
    >
      <div style={{ padding: '40px 48px 0 48px', fontFamily: 'Georgia, serif', color: '#1a1a1a', lineHeight: '1.8', fontSize: '14px' }}>
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.08em', marginBottom: '4px', textTransform: 'uppercase' }}>FREELANCE OVEREENKOMST</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '32px' }}>Opgesteld via Pactorae — pactorae.com</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>PARTIJEN</p>
        <p style={{ marginBottom: '4px' }}><strong>Opdrachtnemer:</strong> Thomas Janssen, Mechelsesteenweg 88, 2018 Antwerpen, BTW BE0456.789.123</p>
        <p style={{ marginBottom: '24px' }}><strong>Opdrachtgever:</strong> Innovatech NV, Leuvenseweg 210, 1000 Brussel, BTW BE0321.654.987</p>

        <p style={{ marginBottom: '16px' }}>Hierna gezamenlijk aangeduid als "de Partijen", komen het volgende overeen:</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 1 — OPDRACHT EN DELIVERABLES</p>
        <p style={{ marginBottom: '8px' }}>Opdrachtnemer verbindt zich ertoe de volgende werkzaamheden uit te voeren voor Opdrachtgever:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Ontwerp en ontwikkeling van een bedrijfswebsite inclusief CMS-integratie</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Implementatie van een klantportaal met inlogfunctionaliteit</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Technische documentatie en overdracht van broncode</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• Twee weken ondersteuning na oplevering</p>

        <p style={{ marginBottom: '16px' }}>De opdracht wordt uitgevoerd in de periode van 1 april 2026 tot en met 30 juni 2026. Tussentijdse oplevering van het klantportaal vindt plaats op 15 mei 2026.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 2 — VERGOEDING EN BETALINGSVOORWAARDEN</p>
        <p style={{ marginBottom: '8px' }}>Opdrachtgever betaalt Opdrachtnemer een totale projectvergoeding van <strong>€ 5.000,00 (vijfduizend euro)</strong>, exclusief BTW (21%).</p>
        <p style={{ marginBottom: '8px' }}>Betaling geschiedt in twee termijnen:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• 50% (€ 2.500,00) bij ondertekening van deze overeenkomst</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• 50% (€ 2.500,00) bij definitieve oplevering van het project</p>

        <p style={{ marginBottom: '16px' }}>Facturen dienen te worden voldaan binnen <strong>30 dagen</strong> na factuurdatum. Bij overschrijding van de betalingstermijn is Opdrachtgever van rechtswege in verzuim en is een vertragingsrente verschuldigd van 1% per maand over het openstaande bedrag, alsmede buitengerechtelijke incassokosten.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 3 — LOOPTIJD EN OPZEGGING</p>
        <p style={{ marginBottom: '16px' }}>Deze overeenkomst treedt in werking op de datum van ondertekening en eindigt van rechtswege op 30 juni 2026 na oplevering van alle deliverables zoals omschreven in Artikel 1.</p>
        <p style={{ marginBottom: '16px' }}>Tussentijdse opzegging is mogelijk met inachtneming van een opzegtermijn van <strong>14 kalenderdagen</strong>. Bij opzegging door Opdrachtgever zijn alle reeds verrichte werkzaamheden verschuldigd op basis van het overeengekomen uurtarief van € 85,00 per uur, exclusief BTW.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 4 — INTELLECTUEEL EIGENDOM</p>
        <p style={{ marginBottom: '16px' }}>Na volledige betaling van de overeengekomen vergoeding draagt Opdrachtnemer alle intellectuele eigendomsrechten op de in het kader van deze overeenkomst ontwikkelde werken over aan Opdrachtgever. Dit omvat broncode, ontwerpen, documentatie en alle overige deliverables.</p>
        <p style={{ marginBottom: '24px' }}>Opdrachtnemer behoudt het recht om het project te vermelden in zijn portfolio, tenzij Opdrachtgever hier schriftelijk bezwaar tegen maakt.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 5 — GEHEIMHOUDING</p>
        <p style={{ marginBottom: '16px' }}>Opdrachtnemer verbindt zich ertoe alle vertrouwelijke informatie van Opdrachtgever strikt geheim te houden en niet te delen met derden, zowel tijdens als na de looptijd van deze overeenkomst. Onder vertrouwelijke informatie wordt verstaan: bedrijfsstrategieën, klantgegevens, financiële informatie, broncode en alle overige informatie die als vertrouwelijk is aangemerkt.</p>
      </div>

      {/* Fade-out gradient overlay */}
      <div
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,1) 100%)',
          marginTop: '-80px',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Below fade message */}
      <div style={{ padding: '0 48px 32px 48px', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' }}>
          Volledig contract genereren met de Pactorae generator
        </p>
        <Link
          to="/contract-generator"
          className="inline-block font-semibold px-8 py-3 rounded-lg text-white transition-colors duration-200 shadow-md hover:shadow-lg"
          style={{ backgroundColor: '#FF8C00' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e07b00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF8C00'}
        >
          Genereer jouw contract gratis
        </Link>
      </div>
    </div>
  </div>
);

const FreelanceContractTemplatePage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>Freelance Contract Template België &amp; Nederland | Pactorae</title>
        <meta
          name="description"
          content="Gratis freelance contract template voor België en Nederland. Professionele freelance overeenkomst voor zelfstandigen en zzp'ers — met betalingsvoorwaarden, intellectueel eigendom en alle standaard clausules. Genereer direct online."
        />
        <meta
          name="keywords"
          content="freelance contract België, freelance contract Nederland, zzp contract template, freelance overeenkomst template, contract generator België, contract generator Nederland, zelfstandige contract"
        />
        <link rel="canonical" href="https://pactorae.com/freelance-contract-template" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/freelance-contract-template" />
        <meta property="og:title" content="Freelance Contract Template België &amp; Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Gratis freelance contract template voor freelancers en zelfstandigen in België en Nederland. Professioneel en juridisch correct."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Freelance Contract Template België &amp; Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis freelance contract template voor freelancers en zzp'ers in België en Nederland. Professioneel en juridisch correct."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#FF8C00]/20 text-[#FF8C00] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#FF8C00]/30">
              Gratis Template
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Freelance Contract Template
              <span className="block text-[#FF8C00] mt-2">België &amp; Nederland</span>
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto">
              Bescherm jezelf als freelancer met een professioneel contract. Gratis template voor freelancers en zelfstandigen in België en Nederland — juridisch correct en direct te gebruiken.
            </p>
            <p className="text-sm text-blue-200 mb-8 max-w-xl mx-auto">
              Geschikt voor zelfstandigen in België, zzp'ers in Nederland, consultants en alle zelfstandige professionals.
            </p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wat is een freelance contract voor België en Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Een freelance contract is een juridisch bindende overeenkomst tussen een freelancer en een opdrachtgever. Of je nu als zelfstandige in België werkt of als zzp'er in Nederland — een goed contract beschermt je bij onbetaalde facturen, scope creep en juridische conflicten. Onze generator maakt contracten op maat voor zowel het Belgisch als het Nederlands recht.
            </p>
          </section>

          {/* What's included */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Wat bevat dit freelance contract template?</h2>
            <ul className="space-y-3">
              {[
                { title: 'Scope van de opdracht', desc: 'Duidelijke omschrijving van de werkzaamheden en deliverables' },
                { title: 'Betalingsvoorwaarden', desc: 'Uurtarief of projectprijs, facturatieritme en betalingstermijnen' },
                { title: 'Looptijd en deadlines', desc: 'Startdatum, einddatum en tussentijdse mijlpalen' },
                { title: 'Intellectueel eigendom', desc: 'Wie bezit de rechten op het geleverde werk?' },
                { title: 'Geheimhouding (NDA)', desc: 'Bescherming van vertrouwelijke informatie van de opdrachtgever' },
                { title: 'Aansprakelijkheid', desc: 'Beperking van aansprakelijkheid en vrijwaring' },
                { title: 'Opzegging en beëindiging', desc: 'Opzegtermijnen en voorwaarden voor vroegtijdige beëindiging' },
                { title: 'Toepasselijk recht', desc: 'Belgisch of Nederlands recht en bevoegde rechtbank' },
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <span className="font-semibold text-[#2F4E6F]">{item?.title}:</span>{' '}
                    <span className="text-gray-600">{item?.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Contract Preview */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Voorbeeld freelance contract</h2>
            <ContractPreview />
          </section>

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-[#2F4E6F] mb-4">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/nda-template', label: 'NDA Template voor Freelancers' },
                { to: '/consultancy-contract-template', label: 'Consultancy Contract Template' },
                { to: '/algemene-voorwaarden-generator', label: 'Algemene Voorwaarden Generator' },
                { to: '/freelance-contract-voorbeeld', label: 'Freelance Contract Voorbeeld' },
              ]?.map((link) => (
                <Link
                  key={link?.to}
                  to={link?.to}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-[#2F4E6F] font-medium text-sm"
                >
                  <span style={{ color: '#FF8C00' }}>→</span> {link?.label}
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-8">Veelgestelde vragen over freelance contracten</h2>
            <div className="space-y-3">
              {faqs?.map((faq, i) => (
                <FaqItem key={i} faq={faq} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw contract te genereren?</h2>
            <p className="text-blue-100 mb-8">Maak in 30 seconden een professioneel freelance contract op maat.</p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </section>
        </article>
      </main>
    </>
  );
};

export default FreelanceContractTemplatePage;
